/**
 * Tailoring a CV to one job posting: what the AI proposes
 * (server/api/ai/tailor-cv.post.ts), how its answer is checked against the
 * CV, and how the proposals the person accepts are applied to the CV.
 *
 * The AI only rewrites what is already there. Skills the posting asks for
 * that the CV does not show come back as `confirmSkills`: they are added only
 * when the person ticks them, i.e. says they really have them. Where they
 * also say what they did with one, that becomes a line under the role, written
 * from their words only (TailorSkillLine).
 *
 * Shared by the server (sanitising) and the editor (applying), and free of
 * Vue/Nuxt imports so both can use it and tests can run it directly.
 */

export interface TailorExperienceEdit {
  /** Position in `cvData.experiences`. */
  index: number
  description: string
  /** One line on why, shown next to the proposal. */
  reason: string
}

export interface TailorProposal {
  /** A headline in the posting's words; "" keeps the current one. */
  title: string
  /** The profile rewritten for this posting; "" keeps the current one. */
  aboutMe: string
  experiences: TailorExperienceEdit[]
  /** The CV's own technical skills, most relevant to the posting first. Empty: keep the order. */
  skillsOrder: string[]
  /** Skills from the posting the CV does not show; added only if the person confirms. */
  confirmSkills: string[]
}

export interface TailorReport extends TailorProposal {
  matchScore: number
  matchingSkills: string[]
  missingSkills: string[]
  /** Free plan: the report without the proposals, and how many there were. */
  limited: boolean
  hiddenProposals: number
}

/**
 * A ticked skill the person also said where they used: one line in their
 * own words (polished by /api/ai/skill-line), added under that role.
 */
export interface TailorSkillLine {
  skill: string
  /** Position in `cvData.experiences`. */
  index: number
  line: string
}

/** What the person accepted: one switch per proposal, plus the ticked skills. */
export interface TailorSelection {
  title: boolean
  aboutMe: boolean
  /** Experience indices whose new description is accepted. */
  experiences: number[]
  skillsOrder: boolean
  confirmedSkills: string[]
  /** Lines for ticked skills, under the role they were used in; optional, a tick alone lists the skill. */
  skillLines?: TailorSkillLine[]
}

interface TailorableCv {
  title?: string
  aboutMe?: string
  experiences?: { description?: string }[]
  technicalSkills?: string[]
}

const MAX_TEXT = 3000
const MAX_LIST = 30

function cleanText(value: unknown, max = MAX_TEXT): string {
  return typeof value === 'string' ? value.trim().slice(0, max) : ''
}

function cleanList(value: unknown, max = MAX_LIST): string[] {
  if (!Array.isArray(value)) return []
  const seen = new Set<string>()
  const out: string[] = []
  for (const item of value) {
    const text = cleanText(item, 120)
    const key = text.toLowerCase()
    if (text && !seen.has(key)) {
      seen.add(key)
      out.push(text)
    }
    if (out.length >= max) break
  }
  return out
}

/**
 * Keeps only proposals that fit the CV they were made for: experience
 * indices that exist and actually change something, a skill order made of
 * the CV's own skills (every one of them, nothing added), and skills to
 * confirm that the CV does not already list.
 */
export function sanitizeProposal(raw: Record<string, unknown>, cv: TailorableCv): TailorProposal {
  const experiences = cv.experiences ?? []
  const skills = (cv.technicalSkills ?? []).filter(skill => typeof skill === 'string' && skill.trim())
  const known = new Map(skills.map(skill => [skill.trim().toLowerCase(), skill]))

  const title = cleanText(raw.title, 200)
  const aboutMe = cleanText(raw.aboutMe)

  const seenIndex = new Set<number>()
  const experienceEdits: TailorExperienceEdit[] = []
  for (const edit of Array.isArray(raw.experiences) ? raw.experiences : []) {
    const item = (edit && typeof edit === 'object' ? edit : {}) as Record<string, unknown>
    const index = typeof item.index === 'number' ? Math.round(item.index) : -1
    const description = cleanText(item.description)
    if (index < 0 || index >= experiences.length || seenIndex.has(index) || !description) continue
    if (description === (experiences[index]?.description ?? '').trim()) continue
    seenIndex.add(index)
    experienceEdits.push({ index, description, reason: cleanText(item.reason, 300) })
  }

  // The order may only rearrange what the CV has: unknown names are dropped,
  // skills the AI left out keep their place at the end.
  const ordered = cleanList(raw.skillsOrder, 200)
    .map(skill => known.get(skill.toLowerCase()))
    .filter((skill): skill is string => Boolean(skill))
  const fullOrder = [...new Set([...ordered, ...skills])]
  const skillsOrder = fullOrder.some((skill, index) => skill !== skills[index]) ? fullOrder : []

  const confirmSkills = cleanList(raw.confirmSkills, 15).filter(skill => !known.has(skill.toLowerCase()))

  return {
    title: title && title !== (cv.title ?? '').trim() ? title : '',
    aboutMe: aboutMe && aboutMe !== (cv.aboutMe ?? '').trim() ? aboutMe : '',
    experiences: experienceEdits,
    skillsOrder,
    confirmSkills,
  }
}

/** How many separate changes a proposal holds (confirmable skills count once). */
export function proposalCount(proposal: TailorProposal): number {
  return (proposal.title ? 1 : 0)
    + (proposal.aboutMe ? 1 : 0)
    + proposal.experiences.length
    + (proposal.skillsOrder.length ? 1 : 0)
    + (proposal.confirmSkills.length ? 1 : 0)
}

/** Every proposal accepted, no extra skill confirmed: what the panel starts from. */
export function defaultSelection(proposal: TailorProposal): TailorSelection {
  return {
    title: Boolean(proposal.title),
    aboutMe: Boolean(proposal.aboutMe),
    experiences: proposal.experiences.map(edit => edit.index),
    skillsOrder: proposal.skillsOrder.length > 0,
    confirmedSkills: [],
    skillLines: [],
  }
}

export const MAX_SKILL_LINE = 300

/** One line, no bullet of its own (the role's list adds it), cut to MAX_SKILL_LINE. */
export function cleanSkillLine(value: unknown): string {
  return cleanText(value, MAX_SKILL_LINE * 2)
    .replace(/\s+/g, ' ')
    .replace(/^[-•*–]\s*/, '')
    .slice(0, MAX_SKILL_LINE)
    .trim()
}

/**
 * The lines that may go into the CV: for a skill among `skills` (the ones
 * ticked), under a role the CV has, one per skill, cleaned.
 */
export function cleanSkillLines(value: unknown, skills: string[], experienceCount: number): TailorSkillLine[] {
  if (!Array.isArray(value)) return []
  const allowed = new Set(skills.map(skill => skill.toLowerCase()))
  const seen = new Set<string>()
  const out: TailorSkillLine[] = []
  for (const item of value.slice(0, MAX_LIST)) {
    const raw = (item && typeof item === 'object' ? item : {}) as Record<string, unknown>
    const skill = cleanText(raw.skill, 120)
    const key = skill.toLowerCase()
    const index = typeof raw.index === 'number' ? Math.round(raw.index) : -1
    const line = cleanSkillLine(raw.line)
    if (!skill || !allowed.has(key) || seen.has(key) || index < 0 || index >= experienceCount || !line) continue
    seen.add(key)
    out.push({ skill, index, line })
  }
  return out
}

/** Each line as one more "- " point at the end of its role's description. The input is not modified. */
export function addSkillLines<T extends TailorableCv>(cv: T, lines: TailorSkillLine[]): T {
  if (!lines.length || !cv.experiences?.length) return cv
  return {
    ...cv,
    experiences: cv.experiences.map((exp, index) => {
      const added = lines.filter(item => item.index === index).map(item => `- ${item.line}`)
      if (!added.length) return exp
      const current = (exp.description ?? '').trimEnd()
      return { ...exp, description: [current, ...added].filter(Boolean).join('\n') }
    }),
  }
}

/** The CV with the accepted proposals applied. The input is not modified. */
export function applyTailoring<T extends TailorableCv>(cv: T, proposal: TailorProposal, selection: TailorSelection): T {
  const next: T = { ...cv }
  if (selection.title && proposal.title) next.title = proposal.title
  if (selection.aboutMe && proposal.aboutMe) next.aboutMe = proposal.aboutMe

  if (selection.experiences.length && cv.experiences) {
    const accepted = new Map(proposal.experiences
      .filter(edit => selection.experiences.includes(edit.index))
      .map(edit => [edit.index, edit.description]))
    next.experiences = cv.experiences.map((exp, index) =>
      accepted.has(index) ? { ...exp, description: accepted.get(index)! } : exp)
  }

  let skills = [...(cv.technicalSkills ?? [])]
  if (selection.skillsOrder && proposal.skillsOrder.length) skills = [...proposal.skillsOrder]
  const confirmed = selection.confirmedSkills.filter(skill =>
    proposal.confirmSkills.includes(skill) && !skills.some(existing => existing.toLowerCase() === skill.toLowerCase()))
  if (confirmed.length) skills = [...skills, ...confirmed]
  if (selection.skillsOrder || confirmed.length) next.technicalSkills = skills

  const ticked = selection.confirmedSkills.filter(skill => proposal.confirmSkills.includes(skill))
  return addSkillLines(next, cleanSkillLines(selection.skillLines, ticked, cv.experiences?.length ?? 0))
}

/**
 * One change an applied tailoring or ATS check made to the CV, so the panel
 * can list what changed, jump to it and undo it.
 */
export type TailorChange =
  | { field: 'title' | 'aboutMe'; before: string; after: string }
  | { field: 'experience'; index: number; before: string; after: string }
  | { field: 'technicalSkills'; before: string[]; after: string[] }

const sameList = (a: string[], b: string[]) => a.length === b.length && a.every((item, index) => item === b[index])

/** What differs between the CV before and after applying, in CV order. */
export function diffTailoring(before: TailorableCv, after: TailorableCv): TailorChange[] {
  const changes: TailorChange[] = []
  for (const field of ['title', 'aboutMe'] as const) {
    if ((before[field] ?? '') !== (after[field] ?? '')) {
      changes.push({ field, before: before[field] ?? '', after: after[field] ?? '' })
    }
  }
  const oldExperiences = before.experiences ?? []
  ;(after.experiences ?? []).forEach((exp, index) => {
    const old = oldExperiences[index]?.description ?? ''
    if ((exp.description ?? '') !== old) changes.push({ field: 'experience', index, before: old, after: exp.description ?? '' })
  })
  const oldSkills = before.technicalSkills ?? []
  const newSkills = after.technicalSkills ?? []
  if (!sameList(oldSkills, newSkills)) changes.push({ field: 'technicalSkills', before: [...oldSkills], after: [...newSkills] })
  return changes
}

/** Skills a change added, as opposed to only moving them around. */
export function addedSkills(change: Extract<TailorChange, { field: 'technicalSkills' }>): string[] {
  const had = new Set(change.before.map(skill => skill.toLowerCase()))
  return change.after.filter(skill => !had.has(skill.toLowerCase()))
}

/** Whether the skills the CV already had now come in a different order. */
export function skillsReordered(change: Extract<TailorChange, { field: 'technicalSkills' }>): boolean {
  const had = new Set(change.before.map(skill => skill.toLowerCase()))
  return !sameList(change.after.filter(skill => had.has(skill.toLowerCase())), change.before)
}

/**
 * Undoes `changes` on the CV as it is now. A field edited by hand since is
 * left alone; for skills only the added ones are taken out and, if the list
 * still matches, the old order comes back. The input is not modified.
 */
export function revertTailoring<T extends TailorableCv>(cv: T, changes: TailorChange[]): T {
  const next: T = { ...cv }
  for (const change of changes) {
    if (change.field === 'title' || change.field === 'aboutMe') {
      if ((cv[change.field] ?? '') === change.after) next[change.field] = change.before
    } else if (change.field === 'experience') {
      const exp = next.experiences?.[change.index]
      if (exp && (exp.description ?? '') === change.after) {
        next.experiences = next.experiences!.map((item, index) =>
          index === change.index ? { ...item, description: change.before } : item)
      }
    } else {
      const current = cv.technicalSkills ?? []
      if (sameList(current, change.after)) {
        next.technicalSkills = [...change.before]
      } else {
        const added = new Set(addedSkills(change).map(skill => skill.toLowerCase()))
        next.technicalSkills = current.filter(skill => !added.has(skill.toLowerCase()))
      }
    }
  }
  return next
}

/** One part of the CV a tailoring changes, as the preview under a new CV lists it. */
export interface TailorPreviewChange {
  part: 'title' | 'aboutMe' | 'experience' | 'skillsOrder' | 'confirmSkills'
  /** Position in `cvData.experiences`, for `experience`. */
  experienceIndex?: number
  /** The AI's one line on what changed, for `experience`. */
  reason?: string
  /** How many skills, for `confirmSkills`. */
  count?: number
  /** The text as it is now and as tailored; withheld on the free preview except for the sample. */
  before?: string
  after?: string
}

/**
 * The tailoring shown under a CV just built (/api/ai/tailor-preview): the
 * score before and after, and one change in full — the proof — with the rest
 * listed but, on the free preview, without their text.
 */
export interface TailorPreview {
  matchScore: number
  /** The AI's estimate with every change applied. */
  matchScoreAfter: number
  /** The same if the visitor also has every skill in `confirmSkills` (see confirmedScore). */
  matchScoreConfirmed: number
  /**
   * Skills the posting asks for that the CV does not show, for the visitor to
   * tick the ones they really have. Sent on the free preview too: the names
   * come from the posting, and ticking them is how the score honestly rises.
   */
  confirmSkills: string[]
  matchingSkills: string[]
  missingSkills: string[]
  /** Missing requirements beyond the ones sent. */
  hiddenMissing: number
  /**
   * How many points of the match those hidden ones are worth, if the visitor
   * has them (see hiddenGain); absent on previews kept before it existed.
   */
  hiddenGain?: number
  /** The change shown in full, before and after; null when the CV already fits. */
  sample: TailorPreviewChange | null
  /** Every other change, in the order the CV shows them. */
  locked: TailorPreviewChange[]
  /** Free preview: `locked` carries no text. */
  limited: boolean
  /**
   * The tailored CV parked for sale as one clean PDF (`tailored-pdf`); null
   * where that product is not on sale. Set by the endpoint, not by tailorPreview.
   */
  tailorToken?: string | null
}

/**
 * Splits a tailoring into the preview: the profile is the sample when it was
 * rewritten (it is the paragraph a recruiter reads first and shows the posting's
 * words best), else the first rewritten role, else the headline. On the free
 * preview the other changes keep only what names them.
 */
export function tailorPreview(
  cv: TailorableCv,
  proposal: TailorProposal,
  match: { matchScore: number; matchScoreAfter: number; matchScoreConfirmed?: number; matchingSkills: string[]; missingSkills: string[] },
  { limited, items }: { limited: boolean; items: number },
): TailorPreview {
  const changes: TailorPreviewChange[] = []
  if (proposal.title) changes.push({ part: 'title', before: cv.title ?? '', after: proposal.title })
  if (proposal.aboutMe) changes.push({ part: 'aboutMe', before: cv.aboutMe ?? '', after: proposal.aboutMe })
  for (const edit of proposal.experiences) {
    changes.push({
      part: 'experience',
      experienceIndex: edit.index,
      reason: edit.reason,
      before: cv.experiences?.[edit.index]?.description ?? '',
      after: edit.description,
    })
  }
  if (proposal.skillsOrder.length) changes.push({ part: 'skillsOrder' })
  // The skills to confirm are not a change made for the visitor but a question to them: `confirmSkills` below.

  const sample = changes.find(c => c.part === 'aboutMe')
    ?? changes.find(c => c.part === 'experience')
    ?? changes.find(c => c.part === 'title')
    ?? null
  const locked = changes
    .filter(c => c !== sample)
    .map(c => (limited ? { part: c.part, experienceIndex: c.experienceIndex, reason: c.reason, count: c.count } : c))

  const matchScoreConfirmed = proposal.confirmSkills.length ? Math.max(match.matchScoreAfter, match.matchScoreConfirmed ?? match.matchScoreAfter) : match.matchScoreAfter

  return {
    matchScore: match.matchScore,
    matchScoreAfter: match.matchScoreAfter,
    matchScoreConfirmed,
    confirmSkills: proposal.confirmSkills,
    matchingSkills: limited ? match.matchingSkills.slice(0, items) : match.matchingSkills,
    missingSkills: limited ? match.missingSkills.slice(0, items) : match.missingSkills,
    hiddenMissing: limited ? Math.max(0, match.missingSkills.length - items) : 0,
    hiddenGain: limited ? hiddenGain(match.missingSkills, items, proposal.confirmSkills, matchScoreConfirmed) : 0,
    sample,
    locked,
    limited,
  }
}

/**
 * The points the hidden missing requirements are worth: what is left to 100
 * once every skill offered for ticking is counted (`matchScoreConfirmed`),
 * shared equally among the missing requirements no tick answers, times the
 * hidden ones among them. An estimate, like the scores it is drawn from: it
 * says what the locked list is worth to someone who really has it all.
 */
function hiddenGain(missing: string[], shown: number, offered: string[], matchScoreConfirmed: number): number {
  const ticked = new Set(offered.map(skill => skill.toLowerCase()))
  const open = missing.map(skill => !ticked.has(skill.toLowerCase()))
  const hidden = open.slice(shown).filter(Boolean).length
  const total = open.filter(Boolean).length
  return total ? Math.round((100 - matchScoreConfirmed) * hidden / total) : 0
}

/** What the page around the result's offers shows of a tailoring run, e.g. a bubble over the CV on phones. */
export interface TailorSummary {
  running: boolean
  matchScore: number | null
  matchScoreAfter: number | null
  /** Changes the tailoring made, shown and locked together. */
  changes: number
}

/**
 * The match with the skills the visitor ticked as theirs: the AI scored the
 * CV with all of them (`matchScoreConfirmed`), and each one ticked fills an
 * equal share of the gap from `matchScoreAfter`. An estimate, like the two
 * scores it sits between, and never more than the visitor said is true.
 */
export function confirmedScore(preview: Pick<TailorPreview, 'matchScoreAfter' | 'matchScoreConfirmed' | 'confirmSkills'>, ticked: number): number {
  const total = preview.confirmSkills.length
  if (!total || ticked <= 0) return preview.matchScoreAfter
  const share = Math.min(ticked, total) / total
  return Math.round(preview.matchScoreAfter + (preview.matchScoreConfirmed - preview.matchScoreAfter) * share)
}

/**
 * The match as the visitor builds it on the free preview: the sample counts
 * as added, each locked change they add fills an equal share of the gap from
 * `matchScore` to `matchScoreAfter`, and each skill ticked as theirs adds its
 * share on top (confirmedScore). With every change added it is exactly
 * confirmedScore: the clicks spread the AI's estimate, they never raise it.
 */
export function liveMatchScore(
  preview: Pick<TailorPreview, 'matchScore' | 'matchScoreAfter' | 'matchScoreConfirmed' | 'confirmSkills' | 'sample' | 'locked'>,
  { added, ticked }: { added: number; ticked: number },
): number {
  const total = preview.locked.length + (preview.sample ? 1 : 0)
  const done = Math.min(total, Math.max(0, added) + (preview.sample ? 1 : 0))
  const missing = total ? (preview.matchScoreAfter - preview.matchScore) * (1 - done / total) : 0
  return Math.round(confirmedScore(preview, ticked) - missing)
}
